<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Overview')" />

      <BaseSectionBody>
        <BaseFieldGroup :label="$t('Title')" :helper-text="$t('Shown to applicants')">
          <div class="tw-w-full">
            <WdsInput
              v-model="store.form.name"
              class="tw-w-full"
              :placeholder="$t('Senior Front-End Engineer')"
              data-test="job-title"
              required
              :error="getErrorMessage(validationStore, 'name')"
              @update:model-value="() => resetExternalValidationErrors('name')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Description')"
          :helper-text="$t('Details about the role and responsibilities')"
          full-width
        >
          <template #label>
            <WdsStack direction-row class="tw-w-full tw-justify-between">
              <WdsText primary large>{{ $t('Description') }}</WdsText>

              <JobDescriptionGenerator
                v-if="aiConsent"
                :company-id="currentAccountId"
                :context="AiJobDescriptionContext.JobPost"
                :job-id="store.persistedJob?.id"
                :title="store.form.name"
                :description="store.form.description"
                @generate-description="handleGeneratedDescription"
              />
            </WdsStack>
            <WdsStack v-if="aiDescriptionUsed" direction-column class="tw-w-full tw-py-2">
              <WdsAlert warning icon>
                {{
                  $t(
                    'AI draft — please review for accuracy, fairness, and legal compliance before using.',
                  )
                }}
              </WdsAlert>
            </WdsStack>
          </template>
          <div class="tw-w-full">
            <WdsInputArea
              v-model="store.form.description"
              :placeholder="$t('Description') + '...'"
              data-test="job-description"
              height="200px"
              required
              :error="getErrorMessage(validationStore, 'description')"
              @update:model-value="() => resetExternalValidationErrors('description')"
            />
          </div>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection v-if="showContactDetails">
      <BaseSectionHeader :header-text="$t('Contact details')" />

      <BaseSectionBody>
        <WdsStack v-if="store.contactProfileIncomplete" direction-column class="tw-w-full tw-pb-2">
          <WdsAlert info icon bordered>
            {{
              $t(
                'Complete your profile so your name, email and phone number fill in here automatically.',
              )
            }}
            <WdsButtonText :href="profileEditUrl">{{ $t('Update your profile') }}</WdsButtonText>
          </WdsAlert>
        </WdsStack>

        <BaseFieldGroup :label="$t('Contact name')" optional>
          <div class="tw-w-full">
            <WdsInput
              v-model="store.form.contactName"
              class="tw-w-full"
              :placeholder="$t('Full name')"
              data-test="job-contact-name"
              :error="getErrorMessage(validationStore, 'contactName')"
              @update:model-value="() => resetExternalValidationErrors('contactName')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup :label="$t('Contact email')" optional>
          <div class="tw-w-full">
            <WdsInput
              v-model="store.form.contactEmail"
              class="tw-w-full"
              :placeholder="$t('name@company.com')"
              data-test="job-contact-email"
              :error="getErrorMessage(validationStore, 'contactEmail')"
              @update:model-value="() => resetExternalValidationErrors('contactEmail')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup :label="$t('Contact phone')" optional>
          <div class="tw-w-full">
            <InputTelephone
              :key="`job-contact-phone-${store.persistedJob?.id ?? 'new'}`"
              :persistent-phone-number="store.form.contactPhone ?? ''"
              :show-verify-button="false"
              :placeholder="$t('+45 12 34 56 78')"
              data-test="job-contact-phone"
              @phone-changed="(value) => (store.form.contactPhone = value ?? '')"
            />
          </div>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection v-show="fieldableStore.fields.length > 0">
      <BaseSectionHeader :header-text="$t('Custom fields')" />
      <BaseSectionBody>
        <FieldableFormCustomFields
          v-model:field-values="store.form.customFieldValues"
          :context-message="customFieldsContextMessage"
          :fieldable-type="TypeSupportingCustomFieldValues.Job"
          :fieldable-id="store.persistedJob?.id"
          :account-id="currentAccountId"
          :should-display-errors="store.hasBeenTouched"
        />
      </BaseSectionBody>
    </BaseSection>

    <BaseSection v-if="singleSupplierId && supplierFields.length > 0">
      <BaseSectionHeader
        :header-text="
          singleSupplierName
            ? $t(':name required fields', { name: singleSupplierName })
            : $t('Supplier required fields')
        "
      />
      <BaseSectionBody>
        <SupplierSharedFieldsForm
          v-model:field-values="store.form.customFieldValues"
          :supplier-id="singleSupplierId"
          :fieldable-type="TypeSupportingCustomFieldValues.Job"
          :fieldable-id="store.persistedJob?.id"
          :account-id="currentAccountId"
          :should-display-errors="store.hasBeenTouched"
        />
      </BaseSectionBody>
    </BaseSection>

    <BaseSection>
      <WdsStack direction-row justify-space-between class="tw-w-full">
        <BaseSectionHeader :header-text="$t('Skills & Experience')" />
        <JobMetadataGenerator
          v-if="aiConsent"
          :job-id="store.persistedJob?.id"
          :title="store.form.name"
          :description="store.form.description"
          @generate-metadata="handleGeneratedMetadata"
        />
      </WdsStack>

      <BaseSectionBody>
        <BaseFieldGroup
          :label="$t('Seniority')"
          :helper-text="$t('What experience level is this job for?')"
        >
          <div class="tw-w-full">
            <WdsSelect
              v-model="selectedExperienceLevel"
              :list="experienceLevelOptions"
              :placeholder="$t('Choose experience level') + '...'"
              data-test="job-experience-level-dropdown"
              :error="getErrorMessage(validationStore, 'expectedExperienceLevel')"
              multi-select
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup :label="$t('Skills')" :helper-text="$t('Used to match candidate profiles')">
          <div class="tw-w-full">
            <TypeSelectSkills
              v-model="computedSkills"
              :skillable-type="[SkillableType.Job]"
              :error="getErrorMessage(validationStore, 'skills')"
              :show-type-suggestions="true"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Industry')"
          :helper-text="$t('Specific industry experience')"
          optional
        >
          <div class="tw-w-full">
            <TypeSelectIndustries
              v-model="computedIndustries"
              can-deselect
              :error="getErrorMessage(validationStore, 'industries')"
            />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Required languages')"
          :helper-text="$t('Which languages are required for this job?')"
          optional
        >
          <div class="tw-w-full">
            <WdsSelect
              v-model="computedLanguages"
              multi-select
              :list="languageOptions"
              :placeholder="$t('Add a language')"
              data-test="job-language-dropdown"
              can-deselect
            />
          </div>
        </BaseFieldGroup>
        <BaseFieldGroup
          v-for="{ name } in store.form.languages"
          :key="name"
          :label="$t(convertEnumToDisplayText(name))"
          :helper-text="$t('What experience level is expected for this language?')"
          :data-test="`job-language-${name.toLowerCase()}-section`"
        >
          <div class="tw-w-full">
            <WdsSelect
              :placeholder="$t('Select experience level')"
              :list="[
                { value: Fluency.Good, label: $t(convertEnumToDisplayText(Fluency.Good)) },
                { value: Fluency.Fluent, label: $t(convertEnumToDisplayText(Fluency.Fluent)) },
              ]"
              :model-value="getLanguageExperience(name)"
              :data-test="`job-language-${name.toLowerCase()}-experience-dropdown`"
              @update:model-value="(value) => updateLanguageExperience(value, name)"
            />
          </div>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection>
      <BaseSectionHeader :header-text="$t('Documents')" />
      <BaseSectionBody>
        <JobAttachments v-model:attachments="store.form.attachments" />
      </BaseSectionBody>
    </BaseSection>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import {
  WdsAlert,
  WdsButtonText,
  WdsInput,
  WdsInputArea,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'

import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import SupplierSharedFieldsForm from '@/components/Fieldable/SupplierSharedFieldsForm.vue'
import TypeSelectSkills from '@/components/TypeSuggestion/TypeSelectSkills.vue'
import TypeSelectIndustries from '@/components/TypeSuggestion/TypeSelectIndustries.vue'
import InputTelephone from '@/components/InputTelephone.vue'
import {
  AiJobDescriptionContext,
  ExpectedExperienceLevel,
  Fluency,
  LanguageName,
  SkillableType,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { convertEnumToDisplayText } from '@/utils/tools'

import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import JobAttachments from '@/components/Job/Edit/Tabs/OverviewTab/JobAttachments.vue'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import useSupplierSharedCustomFields from '@/components/Fieldable/composables/useSupplierSharedCustomFields'
import JobDescriptionGenerator from '@/components/Job/Edit/Tabs/OverviewTab/JobDescriptionGenerator.vue'
import JobMetadataGenerator, {
  GeneratedJobMetadataResponse,
} from '@/components/Job/Edit/Tabs/OverviewTab/JobMetadataGenerator.vue'

const currentAccountId = ref(currentAccount.globalId)
const singleSupplierId = inject<string | undefined>('singleSupplierId', undefined)
const singleSupplierName = inject<string | undefined>('singleSupplierName', undefined)

const { fields: supplierFields } = useSupplierSharedCustomFields(
  currentAccountId.value,
  ref(singleSupplierId),
  [TypeSupportingCustomFieldValues.Job],
)
const store = useJobFormStore()
const fieldableStore = useFieldableFormStore()
const validationStore = useValidationStore()
const { $t } = useTranslation()

const customFieldsContextMessage = computed(() =>
  fieldableStore.fields.some((field) => field.sharedWithStaffingAgencies)
    ? $t(
        'Some details specified below are shared outside your team. The rest are only visible to you and your team members.',
      )
    : $t('Details specified below will only be visible to you and your team members.'),
)

const aiConsent = computed(() => currentAccount?.settings?.ai_consent)

const showContactDetails = inject<boolean>('showContactDetails', false)
const profileEditUrl = laravelNamedRoute('frontend.user.profile.edit-user')

const aiDescriptionUsed = ref(false)

const resetExternalValidationErrors = (name: string) => {
  validationStore.$externalResults[name] = undefined
}

const computedSkills = computed({
  get: () => store.form.skills.map((skillName) => ({ label: skillName, value: skillName })),
  set: (skills) => {
    store.form.skills = skills.map(({ value }) => value)
  },
})

const computedIndustries = computed({
  get: () => store.form.industries.map(({ id, name }) => ({ value: id, label: name })),
  set: (industries) => {
    store.form.industries = industries.map(({ label, value }) => ({ id: value, name: label }))
  },
})

const computedLanguages = computed({
  get: () => store.form.languages.map(({ name }) => ({ value: name })),
  set: (selectedLanguages) => {
    // If a language has been removed
    if (selectedLanguages.length < store.form.languages.length) {
      const deletedLanguage = store.form.languages.find(
        (language) => !selectedLanguages.some(({ value }) => value === language.name),
      )

      store.form.languages = store.form.languages.filter(
        (language) => language.name !== deletedLanguage?.name,
      )
    }

    // If a language has been added
    else {
      const languagesMap = store.form.languages.reduce(
        (accumulator, currentLanguage) => ({
          ...accumulator,
          [currentLanguage.name]: currentLanguage.experience,
        }),
        {},
      )
      store.form.languages = selectedLanguages.map(({ value: languageName }) => ({
        name: languageName,
        experience: languagesMap[languageName] ?? Fluency.Fluent,
      }))
    }
  },
})

const getLanguageExperience = (languageName) => ({
  value: store.form.languages.find((language) => language.name === languageName)?.experience,
})

const updateLanguageExperience = (experience, languageName) => {
  const languageToUpdate = store.form.languages.find((language) => language.name === languageName)

  if (languageToUpdate) {
    languageToUpdate.experience = experience.value
  }
}

const selectedExperienceLevel = computed({
  get: () => store.form.expectedExperienceLevel.map((experience) => ({ value: experience })),
  set: (selected) => {
    store.form.expectedExperienceLevel = selected.map((experience) => experience.value)
  },
})

const experienceLevelOptions = [
  { value: ExpectedExperienceLevel.Student, label: $t('Student') },
  { value: ExpectedExperienceLevel.Junior, label: $t('Junior') },
  { value: ExpectedExperienceLevel.MidLevel, label: $t('Mid-level') },
  { value: ExpectedExperienceLevel.Senior, label: $t('Senior') },
  { value: ExpectedExperienceLevel.Expert, label: $t('Expert') },
]

const languageOptions = Object.values(LanguageName).map((value) => ({
  value,
  label: convertEnumToDisplayText(value),
}))

const handleGeneratedDescription = (description: string) => {
  store.form.description = description
  aiDescriptionUsed.value = true
}

const handleGeneratedMetadata = (response: GeneratedJobMetadataResponse) => {
  // Auto-fill skills
  if (response.skills?.length) {
    store.form.skills = response.skills
  }

  // Auto-fill experience level
  if (response.seniority?.length) {
    store.form.expectedExperienceLevel = response.seniority
  }

  // Auto-fill industries if industry is provided
  if (response.industries?.length) {
    // Assuming industry is a string ID or name that needs to be converted to the right format
    store.form.industries = response.industries
  }
}
</script>
